import { Button, Checkbox, Label, Modal } from "@xola/ui-kit";
import { noop } from "lodash";
import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { Terms, UserRole } from "types";
import { api } from "../../api/api";
import { useUser } from "../../hooks/useAccount";
import { useResource } from "../../hooks/useResource";
import { isLatestTos } from "../../models/TermsOfService";
import { Link } from "../Common/Link";

interface LegalWallModalProps {
    onClose: ({ version }: Pick<Terms, "version">) => void;
    data: Terms;
}

export const LegalWallModal = () => {
    const [isOpen, setIsOpen] = useState(false);
    const user = useUser();

    const isRegularUser = !user.roles.includes(UserRole.ADMIN);

    const { data: terms } = useResource<Terms>(["terms", "latest"]);

    const hasAcceptedLatestTerms =
        user.xolaTermsOfService && isLatestTos(user.xolaTermsOfService.version, terms?.version);

    useEffect(() => {
        if (terms && !hasAcceptedLatestTerms) {
            setIsOpen(true);
        }
    }, [terms, hasAcceptedLatestTerms]);

    const { mutate: acceptTerms } = useMutation(async (payload: Pick<Terms, "version">) => {
        await api.post(`/users/${user.id}/xolaTermsOfService`, payload);

        setIsOpen(false);
    });

    return isRegularUser && terms ? (
        <Modal isOpen={isOpen} size="large" onClose={noop}>
            <ModalContent data={terms} onClose={acceptTerms} />
        </Modal>
    ) : null;
};

const ModalContent = ({ onClose, data }: LegalWallModalProps) => {
    const [isAccepted, setIsAccepted] = useState(false);
    const { text, version } = data;
    const terms = text.replace(/.*class="tc-icon"[^]*div>?/, "");

    return (
        <>
            <Modal.Header>Terms & Conditions Have Changed</Modal.Header>

            <Modal.Body className="space-y-4">
                <div
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: terms }}
                    className="text-center"
                />
                <div className="relative top-5 inline-flex items-center">
                    <Checkbox label="" checked={isAccepted} onChange={(e: any) => setIsAccepted(e.target.checked)} />

                    <Label className="relative left-2 top-0.5 text-base font-normal">
                        <span>
                            I have read and agree to these
                            <Link
                                className="font-bold text-black text-primary underline"
                                url="https://www.xola.com/terms/"
                                target="_blank"
                            >
                                terms and conditions
                            </Link>
                            and
                            <Link
                                className="font-bold text-black text-primary underline"
                                url="https://www.xola.com/privacy-policy/"
                                target="_blank"
                            >
                                privacy policy
                            </Link>
                        </span>
                    </Label>
                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button disabled={!isAccepted} onClick={() => onClose({ version })}>
                    Agree
                </Button>
            </Modal.Footer>
        </>
    );
};
